import { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { AppDialog, useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { useCan } from '#/modules/infra/auth/useCan';
import { ROUTES } from '#/config/routes';
import PurchaseAmendmentStatusBadge from '#/modules/srmMgmt/purchaseOrder/components/PurchaseAmendmentStatusBadge';
import PurchasePaymentPhasesTable from '#/modules/srmMgmt/purchaseOrder/components/PurchasePaymentPhasesTable';
import { buildPurchaseAmendmentLineDiff } from '#/modules/srmMgmt/purchaseOrder/helper/amendmentLineDiff';
import {
  PURCHASE_AMENDMENT_KIND,
  getPurchaseAmendmentKindConfig,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentKinds';
import {
  isPurchaseAmendmentDraft,
  isPurchaseAmendmentSubmitted,
  pickPurchaseAmendmentHistory,
  pickPurchaseAmendmentUpdatedAt,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentStatus';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount, formatDisplayQty } from '#/utils/formatNumber';
import {
  purchaseAmendmentDetailQueryKey,
  purchasePaymentPlanQueryKey,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const CHANGE_LABELS = {
  ADDED: '新增',
  REMOVED: '移除',
  CHANGED: '變更',
  UNCHANGED: '未變',
};

const CHANGE_CLASS = {
  ADDED: 'text-emerald-700',
  REMOVED: 'text-rose-600',
  CHANGED: 'text-sky-700',
  UNCHANGED: 'text-gray-500',
};

function dash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function SummaryCell({ label, value, className }) {
  return (
    <div className={className}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
        {dash(value)}
      </p>
    </div>
  );
}

SummaryCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
};

function BeforeAfterValue({ before, after, format }) {
  const left = before == null ? '—' : format(before);
  const right = after == null ? '—' : format(after);
  if (left === right) {
    return <span>{right}</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      <span className="text-gray-400 line-through">{left}</span>
      <span aria-hidden>→</span>
      <span className="font-medium text-gray-900">{right}</span>
    </span>
  );
}

BeforeAfterValue.propTypes = {
  before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  format: PropTypes.func.isRequired,
};

const CELL = 'px-3 py-2 text-sm tabular-nums';

function LineDiffTable({ kind, rows }) {
  const showCancel = kind === PURCHASE_AMENDMENT_KIND.PURCHASE_ORDER;
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">無明細</p>;
  }
  return (
    <div className="overflow-x-auto rounded-md border border-gray-300">
      <table className="w-full min-w-[40rem] border-collapse">
        <thead className="bg-gray-100 text-xs font-medium text-gray-600">
          <tr>
            <th className={cn(CELL, 'text-left')}>{showCancel ? '物料' : '品名'}</th>
            <th className={cn(CELL, 'text-left')}>變動</th>
            <th className={cn(CELL, 'text-right')}>{showCancel ? '訂購數量' : '數量'}</th>
            {showCancel ? <th className={cn(CELL, 'text-right')}>取消數量</th> : null}
            <th className={cn(CELL, 'text-right')}>單價</th>
            <th className={cn(CELL, 'text-right')}>金額</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row) => (
            <tr key={row.key}>
              <td className={cn(CELL, 'text-left')}>
                <MaterialNameStack
                  name={row.label.name}
                  code={row.label.code}
                  detailPath={row.label.mtrId ? ROUTES.mtrDetail(row.label.mtrId) : null}
                />
              </td>
              <td className={cn(CELL, 'text-left', CHANGE_CLASS[row.change])}>
                {CHANGE_LABELS[row.change]}
              </td>
              <td className={cn(CELL, 'text-right')}>
                <BeforeAfterValue
                  before={row.before?.qty}
                  after={row.after?.qty}
                  format={formatDisplayQty}
                />
              </td>
              {showCancel ? (
                <td className={cn(CELL, 'text-right')}>
                  <BeforeAfterValue
                    before={row.before?.cancelQty}
                    after={row.after?.cancelQty}
                    format={formatDisplayQty}
                  />
                </td>
              ) : null}
              <td className={cn(CELL, 'text-right')}>
                <BeforeAfterValue
                  before={row.before?.unitPrice}
                  after={row.after?.unitPrice}
                  format={formatDisplayAmount}
                />
              </td>
              <td className={cn(CELL, 'text-right')}>
                <BeforeAfterValue
                  before={row.before?.amount}
                  after={row.after?.amount}
                  format={formatDisplayAmount}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

LineDiffTable.propTypes = {
  kind: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/**
 * 採購異動單預覽：明細前後對照、應付總額、擬議付款期別（含已付／待退款）。
 * DRAFT 可進編輯；SUBMITTED 可核准／退回（review 權限）。核准時後端依最新收貨／付款重驗。
 */
const PurchaseAmendmentPreviewDialog = ({
  kind,
  open,
  onOpenChange,
  amendmentId,
  order,
  onMutated,
}) => {
  const config = getPurchaseAmendmentKindConfig(kind);
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectOpen, setRejectOpen] = useState(false);
  const canUpdate = useCan(config.updatePerm);
  const canReview = useCan(config.reviewPerm);
  const orderId = order?.id;

  const detailQuery = useQuery({
    queryKey: purchaseAmendmentDetailQueryKey(kind, orderId, amendmentId),
    queryFn: () => config.fetchAmendmentDetail(orderId, amendmentId),
    enabled: Boolean(open && orderId && amendmentId),
  });

  const amendment = detailQuery.data?.data;
  const history = pickPurchaseAmendmentHistory(amendment);
  const canEditDraft = canUpdate && isPurchaseAmendmentDraft(amendment);
  const canDecide = canReview && isPurchaseAmendmentSubmitted(amendment);
  const lineRows = amendment
    ? buildPurchaseAmendmentLineDiff(kind, amendment.before, amendment.after, order?.lines)
    : [];
  const projection = amendment?.paymentPlanProjection ?? [];
  const hasPlan = Boolean(amendment?.paymentPlan?.phases?.length);

  const mutation = useMutation({
    mutationFn: async ({ action, rejectReason }) => {
      const updatedAt = pickPurchaseAmendmentUpdatedAt(amendment);
      if (!updatedAt) {
        throw new Error('缺少更新時間，請重新載入後再試');
      }
      if (action === 'approve') {
        return config.approveAmendment(orderId, amendmentId, { updatedAt });
      }
      return config.rejectAmendment(orderId, amendmentId, { updatedAt, rejectReason });
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectOpen(false);
      }
      await queryClient.invalidateQueries({ queryKey: config.listQueryKeyPrefix() });
      await queryClient.invalidateQueries({ queryKey: config.detailQueryKey(orderId) });
      await queryClient.invalidateQueries({
        queryKey: purchaseAmendmentDetailQueryKey(kind, orderId, amendmentId),
      });
      // 核准會套用新版付款計畫，詳情頁「付款計畫」頁籤需重讀
      await queryClient.invalidateQueries({ queryKey: purchasePaymentPlanQueryKey(kind, orderId) });
      await onMutated?.();
    },
    onError: (error) => {
      toast.error(config.getErrorMessage(error));
      if (config.isVersionConflict(error)) {
        detailQuery.refetch();
        onMutated?.();
      }
    },
  });

  const isPending = mutation.isPending;

  async function handleApprove() {
    const ok = await confirm({
      title: '核准異動單',
      message: hasPlan
        ? '核准後將同時套用採購變更與新版付款計畫，確定核准？'
        : '核准後將套用採購變更，確定核准？',
      confirmText: '核准',
    });
    if (ok) {
      mutation.mutate({ action: 'approve' });
    }
  }

  const description = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="truncate text-sm font-medium">{amendment?.amendmentNo ?? '—'}</span>
      {amendment?.status ? <PurchaseAmendmentStatusBadge status={amendment.status} size="sm" /> : null}
    </div>
  );

  const footer = (
    <DialogFooter>
      <Button
        type="button"
        variant="outline"
        disabled={isPending}
        onClick={() => onOpenChange(false)}
      >
        關閉
      </Button>
      {canEditDraft && orderId && amendmentId ? (
        <Button type="button" variant="outline" asChild>
          <Link to={config.editRoute(orderId, amendmentId)}>編輯</Link>
        </Button>
      ) : null}
      {canDecide ? (
        <Button
          type="button"
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={() => setRejectOpen(true)}
        >
          退回
        </Button>
      ) : null}
      {canDecide ? (
        <Button type="button" disabled={isPending} onClick={handleApprove}>
          核准
        </Button>
      ) : null}
    </DialogFooter>
  );

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="退回異動單"
        description="退回後原採購單與付款計畫維持不變；需重新異動請另建草稿。"
        confirmText="退回"
        isPending={isPending}
        onConfirm={(rejectReason) => mutation.mutate({ action: 'reject', rejectReason })}
      />
      <AppDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isPending) {
            return;
          }
          onOpenChange(nextOpen);
        }}
        title="異動單預覽"
        description={description}
        headerVariant="primary"
        showCloseButton
        maxWidthClassName={dialogWideMaxWidthClassName}
        contentClassName={dialogContentCompactGapClassName}
        closeOnOutsideClick={!isPending}
        closeOnEscape={!isPending}
        footer={footer}
      >
        {detailQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">載入中…</p>
        ) : null}
        {detailQuery.isError ? (
          <p className="text-sm text-destructive">
            {config.getErrorMessage(detailQuery.error, '找不到異動單資料')}
          </p>
        ) : null}
        {amendment ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-3">
              <SummaryCell
                label="事由"
                value={amendment.issueReason}
                className="sm:col-span-3"
              />
              {amendment.remark ? (
                <SummaryCell label="備註" value={amendment.remark} className="sm:col-span-3" />
              ) : null}
              <SummaryCell
                label="建立時間"
                value={formatDateTimeOrDash(amendment.record?.createdAt)}
              />
              <SummaryCell label="提交時間" value={formatDateTimeOrDash(history.submittedAt)} />
              <SummaryCell
                label={history.rejectedAt ? '退回時間' : '核准時間'}
                value={formatDateTimeOrDash(history.rejectedAt ?? history.approvedAt)}
              />
              {history.rejectReason ? (
                <SummaryCell
                  label="退回原因"
                  value={history.rejectReason}
                  className="sm:col-span-3"
                />
              ) : null}
            </div>

            <section className="space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-medium text-gray-900">採購明細</h3>
                <p className="text-sm text-gray-600">
                  {'應付總額 '}
                  <BeforeAfterValue
                    before={amendment.before?.totalAmount}
                    after={amendment.after?.totalAmount}
                    format={formatDisplayAmount}
                  />
                </p>
              </div>
              <LineDiffTable kind={kind} rows={lineRows} />
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-medium text-gray-900">付款計畫</h3>
              {hasPlan ? (
                <>
                  {amendment.paymentPlan?.changeReason ? (
                    <p className="text-sm text-gray-600">
                      {`調整原因：${amendment.paymentPlan.changeReason}`}
                    </p>
                  ) : null}
                  <PurchasePaymentPhasesTable
                    phases={projection.length ? projection : amendment.paymentPlan.phases}
                    variant="projection"
                  />
                </>
              ) : (
                <p className="text-sm text-muted-foreground">未調整，沿用目前有效付款計畫。</p>
              )}
            </section>
          </div>
        ) : null}
      </AppDialog>
    </>
  );
};

PurchaseAmendmentPreviewDialog.propTypes = {
  kind: PropTypes.oneOf(Object.values(PURCHASE_AMENDMENT_KIND)).isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  amendmentId: PropTypes.string,
  order: PropTypes.shape({
    id: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
  }),
  onMutated: PropTypes.func,
};

export default PurchaseAmendmentPreviewDialog;
